/* global plausible, document, window */

/**
 * @typedef {object} AttributionCookieProps
 * @property {string} firstPage
 * @property {string} signupSource
 * @property {string} coupon
 * @property {string} lastPage
 */

/**
* Read UTM campaign data and save in cookie or update existing
*/
function attributionCookies(document) {
    const NINETY_DAYS = 1000 * 60 * 60 * 24 * 90;

    /**
     * Takes a cookie name and returns the value of the cookie
     * @param {string} cookieName
     * @returns {object|null}
     */
    const getCookie = (cookieName) => {
        if (!cookieName) {
            return null;
        }

        // Add the = sign
        cookieName = cookieName + '=';
        // Get the decoded cookie
        const decodedCookie = decodeURIComponent(document.cookie);
        // Get all cookies, split on ; sign
        const cookies = decodedCookie.split(';');
        // Loop over the cookies
        for (let i = 0; i < cookies.length; i++) {
            // Define the single cookie, and remove whitespace
            const cookie = cookies[i].trim();
            // If this cookie has the name of what we are searching
            if (cookie.indexOf(cookieName) === 0) {
                // Return everything after the cookies name
                try {
                    return JSON.parse(cookie.substring(cookieName.length, cookie.length));
                } catch (error) {
                    return null;
                }
            }
        }
    };

    /**
     * Takes a cookie name, a value and a duration and sets the cookie
     * @param {string} cookieName
     * @param {object} value - will be converted to JSON
     * @param {number?} duration - defaults to 90 days
     * @returns {string} - the set cookie
     */
    const setCookie = (cookieName, value, duration = NINETY_DAYS) => {
        const expiry = new Date(Date.now() + duration);
        const hostname = window.location.hostname;
        const encodedValue = encodeURIComponent(JSON.stringify(value));

        let domain = '.ghost.org';
        let options = '; path=/; expires=' + expiry.toGMTString() + '; samesite=strict';

        // Skip .ghost.org domain restriction for localhost, staging and deploy previews
        if (hostname.endsWith('.ghostfoundation.org')) {
            domain = '.ghostfoundation.org';
        } else if (hostname === 'localhost' || hostname.endsWith('.netlify.app')) {
            domain = '';
        }

        if (domain) {
            options += `; domain=${domain}`;
        }

        if (window.location.protocol === 'https:') {
            options += '; secure';
        }

        document.cookie = cookieName + '=' + encodedValue + options;
        return document.cookie;
    };

    /**
     * Reads defined UTM campaign data and returns it as an object
     * @returns {object|null}
     */
    const readCampaignData = () => {
        let campaign = null;
        // Check, if campaign information got passed via URL and
        // save this information later in a cookie
        const urlParams = new URLSearchParams(window.location.search);

        if (urlParams.has('utm_source') || urlParams.has('utm_medium') || urlParams.has('utm_campaign') || urlParams.has('twclid')) {
            campaign = {};

            // store the query params so we can pass them to the server
            urlParams.forEach((value, key) => {
                campaign[key] = value;
            });
        }

        return campaign;
    };

    /**
     * @returns {AttributionCookieProps}
     * @todo Add reading FirstPromoter data to here (coupon, FP referrer id, etc.),
     *      see https://linear.app/tryghost/issue/CA-8/allow-external-tools-to-set-coupons
     */
    const readAttributionData = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const coupon = urlParams.get('cpn') ?? null;
        const utmSource = urlParams.get('utm_source') ?? null;
        // if we can't find a referrer, let's see if we have a utm_source
        const signupSource = (document.referrer || utmSource) ?? null;
        const firstPage = window.location.pathname;

        return {
            firstPage,
            signupSource,
            coupon
        };
    };

    /**
     * Catch last page before leaving the site and update the attribution cookie
     * @returns {void}
     */
    const listenForLastPageAction = () => {
        const allSignupCTAs = document.getElementsByClassName('plausible-event-name=Signup-CTA-clicked');

        for (let index = 0; index < allSignupCTAs.length; index++) {
            const cta = allSignupCTAs[index];
            const originalClickHandler = cta.onclick;
            // Before leaving this site we want to store the last page the user visited
            // and update the attribution cookie
            cta.addEventListener('click', (e) => {
                e.preventDefault();
                try {
                    let attributionCookie = getCookie('_ghost_attr');
                    if (typeof attributionCookie === 'string') {
                        // if the cookie is a string, we need to parse it
                        attributionCookie = JSON.parse(attributionCookie);
                    }
                    if (attributionCookie) {
                        const lastPage = window.location.pathname;
                        // always update the last page before signup
                        attributionCookie.lastPage = lastPage;
                        setCookie('_ghost_attr', attributionCookie);
                    } else {
                        // set the cookie in case it doesn't exist yet
                        const attribution = readAttributionData();
                        const lastPage = window.location.pathname;
                        // always update the last page before signup
                        attribution.lastPage = lastPage;
                        setCookie('_ghost_attr', attribution);
                    }
                } catch (error) {
                    // noop
                }
                // trigger the original event
                if (originalClickHandler) {
                    originalClickHandler.call(cta, e);
                } else if (cta.getAttribute('href')) {
                    window.location.href = cta.getAttribute('href');
                }
            });
        }
    };

    // Handle UTM campaigns incl. Twitter Pixel
    let campaign = readCampaignData();

    if (campaign) {
        const utmCookieOld = getCookie('_utm');

        if (utmCookieOld) {
            // Update only changed properties in our cookie
            campaign = Object.assign({}, utmCookieOld, campaign);
        }

        setCookie('_utm', campaign);
    }

    let currentAttribution = readAttributionData();

    if (currentAttribution) {
        let existingAttribution = getCookie('_ghost_attr');

        if (existingAttribution) {
            if (typeof existingAttribution === 'string') {
                // if the cookie is a string, we need to parse it
                existingAttribution = JSON.parse(existingAttribution);
            }
            // update any unset values on the existing cookie
            if (currentAttribution.coupon && !existingAttribution.coupon) {
                existingAttribution.coupon = currentAttribution.coupon;
            }
            if (currentAttribution.signupSource && !existingAttribution.signupSource) {
                existingAttribution.signupSource = currentAttribution.signupSource;
            }
            if (currentAttribution.firstPage && !existingAttribution.firstPage) {
                existingAttribution.firstPage = currentAttribution.firstPage;
            }
            setCookie('_ghost_attr', existingAttribution);
        } else {
            setCookie('_ghost_attr', currentAttribution);
        }
    }

    if (typeof plausible !== 'undefined' || (typeof window !== 'undefined' && typeof window.plausible !== 'undefined')) {
        var plsbl = plausible || window.plausible;
        plsbl('pageview');
    }

    listenForLastPageAction();
}

document.addEventListener('DOMContentLoaded', () => {
    attributionCookies(document);
});
